import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
    InputStyle,
    isWidget,
    noticeError,
    InputSelector,
    QuestionCompact,
    CREDIT_SCORES,
    BOOLEAN_OPTIONS,
    CREDIT_SCORES_OPTIONS,
    Config,
} from '@ratehub/base-ui';
import {
    getCreditCardSortOptions,
    SCENARIOS,
    REWARD_TYPES,
    USER_CATEGORIES,
    ANNUAL_FEE_OPTIONS,
    REWARD_SORT_OPTIONS,
    LOW_INTEREST_SORT_OPTIONS,
} from '@ratehub/cc-common';

import PRODUCT_TABLE_MESSAGES from '../definitions/ProductTableMessages';


function getCreditCardFilteredAsyncContainerFilters({
    scenario,
    rewardType,
    noAnnualFee,
    monthlySpendingAverage,
    income,
    creditScore,
    initialRewardType,
    initialUserCategory,
    monthlyCarryOverBalance,
    transferredBalance,
    requiresGiftOffer,
    isOptimizedForMobile,
}) {
    const annualFee = noAnnualFee
        ? ANNUAL_FEE_OPTIONS.NO_FEE
        : ANNUAL_FEE_OPTIONS.ANY_FEE;

    switch (scenario) {
        case SCENARIOS.REWARDS:
            return getRewardCardsFilters({
                rewardType,
                annualFee,
                monthlySpendingAverage,
                income,
                creditScore,
                initialRewardType,
                initialUserCategory,
                requiresGiftOffer,
                isOptimizedForMobile,
            });

        case SCENARIOS.LOW_INTEREST:
        case SCENARIOS.BALANCE_TRANSFER:
            // the filters for these scenarios differ only in the very first filter
            return getLowInterestOrBalanceTransferFilters({
                scenario,
                monthlyCarryOverBalance,    // LI only
                transferredBalance,         // BT only
                annualFee,
                creditScore,
                isOptimizedForMobile,
            });

        case SCENARIOS.SECURED:
            return getSecuredFilters({
                annualFee,
                requiresGiftOffer,
            });

        default:
            noticeError(new RangeError(`[getCreditCardFilteredAsyncContainerFilters] unexpected scenario value: ${scenario}`));
            // return empty filter arrays so the page won’t crash
            return {
                primaryFilters: [],
                additionalFilters: [],
            };
    }
}

function getRewardCardsFilters({
    rewardType,
    annualFee,
    monthlySpendingAverage,
    income,
    creditScore,
    initialRewardType,
    initialUserCategory,
    requiresGiftOffer,
    isOptimizedForMobile,
}) {
    // omit `Reward type` filter for BUSINESS users by substituting `null`
    // that it keeps the length of the primaryFilters as 4 to match additionalFilters to keep it on one line
    // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
    const RewardTypeQuestion = initialUserCategory !== USER_CATEGORIES.BUSINESS
        ? (
            <QuestionCompact
                key="rewardType"
                field={{
                    name: 'rewardType',
                    type: 'choice',
                    value: rewardType ?? REWARD_TYPES.ALL,
                    options: Object.values(REWARD_TYPES),
                }}
                renderOptionLabel={value => (
                    <FormattedMessage
                        {...PRODUCT_TABLE_MESSAGES[value]}
                    />
                )}
                labelMessage={(
                    <FormattedMessage
                        id="web-components.cc-product-list.reward-type.label"
                        defaultMessage="Reward type"
                    />
                )}
                type="dropdown"
                className="filter-question-compact"
            />
        )
        : null;

    const primaryFilters = [
        <QuestionCompact
            key="monthlySpendingAverage"
            field={{
                name: 'monthlySpendingAverage',
                type: 'integer',
                value: monthlySpendingAverage,
            }}
            labelMessage={(
                <FormattedMessage
                    id="web-components.cc-product-list.monthly-expenses.label"
                    defaultMessage="Monthly expenses"
                />
            )}
            type="currency"
            className="filter-question-compact"
        />,
        <SortQuestion
            key="sortBy"
            options={getCreditCardSortOptions(SCENARIOS.REWARDS)}
            defaultValue={isWidget()
                ? REWARD_SORT_OPTIONS.SUBSEQUENT_YEAR_VALUE
                : REWARD_SORT_OPTIONS.RECOMMENDED
            }
        />,
    ];

    const additionalFilters = [
        <IncomeQuestion
            key="income"
            income={income}
        />,
        <CreditScoreQuestion
            key="creditScore"
            creditScore={creditScore}
        />,
    ];

    // To allow conditionally rendering the first question in the primary filters based on the initial reward type
    if (initialRewardType === REWARD_TYPES.ALL) {
        primaryFilters.unshift(RewardTypeQuestion);     // will be null if user category is BUSINESS
        additionalFilters.unshift(
            <AnnualFeeQuestion
                key="annualFee"
                annualFee={annualFee}
            />,
        );
    } else {
        primaryFilters.unshift(
            <AnnualFeeQuestion
                key="annualFee"
                annualFee={annualFee}
            />,
        );
        additionalFilters.unshift(RewardTypeQuestion);
    }

    if (Config.ENABLE_CC_GIFT_OFFER_FILTER) {
        // Has gift offer question always first in primaryFilters
        primaryFilters.unshift(
            <HasGiftOfferQuestion
                key="has-gift-offer"
                requiresGiftOffer={requiresGiftOffer}
            />,
        );
    }

    if (isOptimizedForMobile) {
        // pluck the first two filters from the start of primaryFilters and insert at the start of additionalFilters
        additionalFilters.unshift(...primaryFilters.splice(0, 2));
    }

    return {
        primaryFilters,
        additionalFilters,
    };
}

function getLowInterestOrBalanceTransferFilters({
    scenario,
    monthlyCarryOverBalance,
    transferredBalance,
    annualFee,
    creditScore,
    isOptimizedForMobile,
}) {
    // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
    const BalanceFilterQuestion = scenario === SCENARIOS.LOW_INTEREST
        ? (
            <QuestionCompact
                key="monthlyCarryOverBalance"
                field={{
                    name: 'monthlyCarryOverBalance',
                    type: 'integer',
                    value: monthlyCarryOverBalance,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="web-components.cc-product-list.monthly-carry-over-balance.label"
                        defaultMessage="Monthly carry over balance"
                    />
                )}
                type="currency"
                className="filter-question-compact"
            />
        )
        : (
            <QuestionCompact
                key="transferredBalance"
                field={{
                    name: 'transferredBalance',
                    type: 'integer',
                    value: transferredBalance,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="web-components.cc-product-list.transferred-balance-balance.label"
                        defaultMessage="Transferred balance"
                    />
                )}
                type="currency"
                className="filter-question-compact"
            />
        );

    const primaryFilters = [
        BalanceFilterQuestion,  // either a monthlyCarryOverBalance (LI)- or transferredBalance (BT)-based Question

        <AnnualFeeQuestion
            key="annualFee"
            annualFee={annualFee}
        />,

        <SortQuestion
            key="sortBy"
            options={getCreditCardSortOptions(SCENARIOS.LOW_INTEREST)}
            defaultValue={LOW_INTEREST_SORT_OPTIONS.PURCHASE_INTEREST_RATE}
        />,
    ];

    const additionalFilters = [
        <CreditScoreQuestion
            key="creditScore"
            creditScore={creditScore}
        />,
    ];

    if (isOptimizedForMobile) {
        // pluck the Annual fee from the second position in primary and make it the first in additional
        additionalFilters.unshift(primaryFilters.splice(1, 1));
    }

    return {
        primaryFilters,
        additionalFilters,
    };
}

// Get the filters for Secured cards
function getSecuredFilters({ annualFee, requiresGiftOffer }) {
    const primaryFilters = [
        <HasGiftOfferQuestion
            key="has-gift-offer"
            requiresGiftOffer={requiresGiftOffer}
        />,

        <AnnualFeeQuestion
            key="annualFee"
            annualFee={annualFee}
        />,
    ];

    return {
        primaryFilters,
        additionalFilters: [],
    };
}

// eslint-disable-next-line react/prop-types
function AnnualFeeQuestion({ annualFee }) {
    return (
        <QuestionCompact
            field={{
                name: 'annualFee',
                type: 'choice',
                value: annualFee,
                options: Object.values(ANNUAL_FEE_OPTIONS),
            }}
            renderOptionLabel={value => (
                <FormattedMessage
                    {...PRODUCT_TABLE_MESSAGES[value]}
                />
            )}
            labelMessage={(
                <FormattedMessage
                    id="web-components.cc-product-list.annual-fee.label"
                    defaultMessage="Annual fee"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />
    );
}

// eslint-disable-next-line react/prop-types
function IncomeQuestion({ income }) {
    return (
        <QuestionCompact
            field={{
                name: 'income',
                type: 'integer',
                value: income,
            }}
            labelMessage={(
                <FormattedMessage
                    id="web-components.cc-product-list.income.label"
                    defaultMessage="Income"
                />
            )}
            type="currency"
            className="filter-question-compact"
        />
    );
}

// eslint-disable-next-line react/prop-types
function CreditScoreQuestion({ creditScore }) {
    return (
        <QuestionCompact
            field={{
                name: 'creditScore',
                type: 'choice',
                value: creditScore,
                options: CREDIT_SCORES_OPTIONS.filter(option => option.value !== CREDIT_SCORES.NONE),
            }}
            labelMessage={(
                <FormattedMessage
                    id="web-components.cc-product-list.credit-score.label"
                    defaultMessage="Credit score"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />
    );
}

// eslint-disable-next-line react/prop-types
function SortQuestion({ options, defaultValue }) {
    return (
        <QuestionCompact
            key="sortBy"
            field={{
                name: 'sortBy',
                type: 'choice',
                value: defaultValue,
                options: Object.values(options),
            }}
            labelMessage={(
                <FormattedMessage
                    id="web-components.cc-product-list.sort-by.label"
                    defaultMessage="Sort by"
                />
            )}
            renderOptionLabel={value => (
                <FormattedMessage
                    {...PRODUCT_TABLE_MESSAGES[value]}
                />
            )}
            type="dropdown"
            className="filter-question-compact"

            data-name="toggle-sort-by"
        />
    );
}

function HasGiftOfferQuestion({ requiresGiftOffer }) {
    return (
        <HasGiftOfferWrapper className="rh-display-flex rh-align-items-center">
            <InputSelector
                type="checkbox"
                field={{
                    name: 'requiresGiftOffer',
                    type: 'boolean',
                    value: requiresGiftOffer,
                    options: BOOLEAN_OPTIONS,
                }}
                label={(
                    <FormattedMessage
                        id="web-components.cc-product-list.has-gift-offer.label"
                        defaultMessage="Has a gift offer"
                    />
                )}
                // without the padding, the checkbox container is vertically squished on mobile
                // this provides an imperfect, but very close, correction (47px vs 49px)
                className="filter-question-compact rh-py-0_75"
                key="has-gift-card"
            />
        </HasGiftOfferWrapper>
    );
}

const HasGiftOfferWrapper = styled.div`
    ${InputStyle};
    height: auto;
`;

HasGiftOfferQuestion.propTypes = {
    requiresGiftOffer: PropTypes.bool.isRequired,
};


export default getCreditCardFilteredAsyncContainerFilters;
